import { useState, useCallback, useContext, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { VisitFormData, Task, TaskFormData, VisitDocument, PendingUploadFile, ActiveGuest } from '../../../../interfaces/property-visit.interface';
import { Validation } from '../../../../interfaces/common';
import { Modules, RouteParamsTypes, RoleCodes } from '../../../../enums';
import {
  VALIDATION_FIELD_MAPPING, APP_SUCCESS_MESSAGES, APP_ERROR_MESSAGES,
  PROPERTY_VISIT_STATUS, QUICK_VISIT_TYPE_ID, GUEST_STAY_VISIT_TYPE_ID, GENERIC_LOOKUP_KEYS,
  PROPERTY_VISIT_ROLE_TO_SYSTEM_ROLE,
} from '../../../../utils/appConstant';
import { formatDate, computeVisitDateBounds } from '../../../../utils/methods';
import useFetch from '../../../../hooks/useFetch';
import PropertyService from '../../../../services/property.service';
import PropertyVisitService from '../../../../services/property-visit.service';
import CommonService from '../../../../services/common.service';
import VendorService from '../../../../services/vendor.service';
import RoleService from '../../../../services/role.service';
import { AuthContext } from '../../../../contexts/AuthContext';
import Toast from '../../../../components/Toast';
import { validateVisitForm, validateTaskForm } from '../validations';

export type TabType = 'visit' | 'documents' | 'tasks';

const initialVisitFormData: VisitFormData = {
  propertyId: '',
  visitDate: formatDate(new Date()),
  visitType: '',
  userRole: '',
  visitedBy: '',
  visitStatus: '',
  visitNote: '',
  internalNote: '',
  internalStatus: PROPERTY_VISIT_STATUS.toBeReviewed,
};

const initialTaskFormData: TaskFormData = {
  description: '',
  category: '',
  priority: '',
  status: 'pending',
  due_date: '',
};

const initialValidation: Validation = { validate: true, fields: [] };

const initialDropdownSearchData = {
  propertyId: '',
  visitType: '',
  userRole: '',
  visitedBy: '',
  visitStatus: '',
  internalStatus: '',
};

const initialTaskDropdownSearchData = {
  category: '',
  priority: '',
  status: '',
};

export const useAddEditViewController = () => {
  useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { isSystemAdmin, loggedInUser } = useContext(AuthContext);

  const isAdmin = isSystemAdmin === 'true' || loggedInUser?.role_code === RoleCodes.admin;
  const isInspector = loggedInUser?.role_code === RoleCodes.inspector;
  const isManager = loggedInUser?.role_code === RoleCodes.manager;
  const isAddMode = !id;

  // Visit raw data (edit/view mode)
  const [visitRaw, setVisitRaw] = useState<any>(null);
  const [visitLoading, setVisitLoading] = useState(!isAddMode);

  // Tab state — read initial tab from URL ?tab= param
  const initialTab = (searchParams.get('tab') as TabType) || 'visit';
  const [activeTab, setActiveTab] = useState<TabType>(isAddMode ? 'visit' : initialTab);

  // Visit form (Tab 1)
  const [visitFormData, setVisitFormData] = useState<VisitFormData>({
    ...initialVisitFormData,
    userRole: isInspector ? RoleCodes.inspector : isManager ? 'property_manager' : '',
    visitedBy: (isInspector || isManager) ? (loggedInUser?.id ?? '') : '',
  });
  const [visitValidation, setVisitValidation] = useState<Validation>(initialValidation);
  const [visitSaveLoading, setVisitSaveLoading] = useState(false);

  // Documents (Tab 2)
  const [existingDocs, setExistingDocs] = useState<VisitDocument[]>([]);
  const [reportUrl, setReportUrl] = useState('');
  const [reportUrlError, setReportUrlError] = useState('');
  const [pendingPdfFile, setPendingPdfFile] = useState<File | null>(null);
  const [pendingImageFiles, setPendingImageFiles] = useState<PendingUploadFile[]>([]);
  const [docLoading, setDocLoading] = useState(false);

  // Tasks (Tab 3)
  const [tasks, setTasks] = useState<Task[]>([]);
  const [pendingTasksCount, setPendingTasksCount] = useState<number>(0);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskDeleteModalOpen, setTaskDeleteModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  const [taskFormData, setTaskFormData] = useState<TaskFormData>(initialTaskFormData);
  const [taskValidation, setTaskValidation] = useState<Validation>(initialValidation);
  const [taskLoading, setTaskLoading] = useState(false);

  // Shared
  const [dropdownSearchData, setDropdownSearchData] = useState(initialDropdownSearchData);
  const [taskDropdownSearchData, setTaskDropdownSearchData] = useState(initialTaskDropdownSearchData);
  const [activeGuest, setActiveGuest] = useState<ActiveGuest | null>(null);
  const autoSetVisitTypeRef = useRef(false);

  // Derived mode flags
  const currentStatus = visitRaw?.status ?? '';
  const isFormEditable = isAddMode || isAdmin || [PROPERTY_VISIT_STATUS.toBeReviewed, PROPERTY_VISIT_STATUS.needsClarification].includes(currentStatus);
  const tabsDisabled = isAddMode;
  const internalNoteRequired = [PROPERTY_VISIT_STATUS.needsClarification, PROPERTY_VISIT_STATUS.notQualified].includes(visitFormData.internalStatus);

  // Reset all form state when navigating (e.g. Add New from edit page)
  useEffect(() => {
    setVisitRaw(null);
    setVisitFormData({
      ...initialVisitFormData,
      userRole: isInspector ? RoleCodes.inspector : isManager ? 'property_manager' : '',
      visitedBy: (isInspector || isManager) ? (loggedInUser?.id ?? '') : '',
    });
    setVisitValidation(initialValidation);
    setExistingDocs([]);
    setReportUrl('');
    setReportUrlError('');
    setPendingPdfFile(null);
    setPendingImageFiles([]);
    setTasks([]);
    setPendingTasksCount(0);
    setDropdownSearchData(initialDropdownSearchData);
    setTaskDropdownSearchData(initialTaskDropdownSearchData);
    setActiveGuest(null);
    setActiveTab((searchParams.get('tab') as TabType) || 'visit');
    autoSetVisitTypeRef.current = false;
  }, [location.key]);

  // Load visit on edit/view
  useEffect(() => {
    if (!id) return;
    setVisitLoading(true);
    PropertyVisitService.get(id)
      .then((data: any) => {
        if (data) {
          setVisitRaw(data);
          const rawDate = data.visit_date ?? '';
          const [m, d, y] = rawDate.split('/');
          const isoDate = m && d && y ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : rawDate;
          setVisitFormData({
            propertyId: data.property?.id?.toString() || '',
            visitDate: isoDate,
            visitType: data.visit_type || '',
            userRole: data.user_role || '',
            visitedBy: data.visited_by?.id?.toString() || '',
            visitStatus: data.status || '',
            visitNote: data.visit_note === '-' ? '' : (data.visit_note || ''),
            internalNote: data.internal_note || '',
            internalStatus: data.status || '',
          });
          // Load documents
          const docs: VisitDocument[] = (data.documents ?? []).map((d: any) => ({
            id: d.id?.toString() ?? '',
            document_type: d.document_type,
            path: d.path ?? '',
            file_url: d.file_url ?? '',
          }));
          setExistingDocs(docs);
          const urlDoc = docs.find((d) => d.document_type === 'url');
          if (urlDoc) setReportUrl(urlDoc.path);
          setPendingTasksCount(data.pending_tasks_count ?? 0);
        }
      })
      .finally(() => setVisitLoading(false));
  }, [id, location.key]);

  // Load tasks on edit
  useEffect(() => {
    if (!id) return;
    PropertyVisitService.getVisitTasks(id).then(setTasks);
  }, [id, location.key]);

  // Guest detection
  useEffect(() => {
    const propertyId = visitFormData.propertyId;
    if (!propertyId || !visitFormData.visitDate) { setActiveGuest(null); return; }
    PropertyVisitService.checkActiveGuest(propertyId, visitFormData.visitDate).then((guest) => {
      setActiveGuest(guest);
      if (guest) {
        setVisitFormData((prev) => {
          if (!prev.visitType || autoSetVisitTypeRef.current) {
            autoSetVisitTypeRef.current = true;
            return { ...prev, visitType: GUEST_STAY_VISIT_TYPE_ID };
          }
          return prev;
        });
      } else if (autoSetVisitTypeRef.current) {
        setVisitFormData((prev) => ({ ...prev, visitType: '' }));
        autoSetVisitTypeRef.current = false;
      }
    });
  }, [visitFormData.propertyId, visitFormData.visitDate]);

  // --- Lookups ---
  const { data: allProperties } = useFetch({ service: PropertyService, func: 'lookup', payload: { searchText: '', is_active: '1' }, dependency: [] });
  const { data: properties } = useFetch({ service: PropertyService, func: 'lookup', payload: { searchText: dropdownSearchData.propertyId, is_active: '1' }, dependency: [dropdownSearchData.propertyId] });
  const { data: allVisitTypes } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitType, searchText: '' }, dependency: [] });
  const { data: visitTypes } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitType, searchText: dropdownSearchData.visitType }, dependency: [dropdownSearchData.visitType] });
  const { data: allUserRoles } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitUserRole, searchText: '' }, dependency: [] });
  const { data: userRoles } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitUserRole, searchText: dropdownSearchData.userRole }, dependency: [dropdownSearchData.userRole] });
  const { data: allServiceTypes } = useFetch({ service: VendorService, func: 'serviceTypesLookup', payload: { searchText: '' }, dependency: [] });
  const cleanerServiceTypeId = ((allServiceTypes as any[]) || []).find((s: any) => s.name?.toLowerCase() === 'cleaner')?.id?.toString() ?? '';

  const { data: allRolesLookup } = useFetch({ service: RoleService, func: 'lookup', payload: { searchText: '' }, dependency: [] });
  const systemRoleName = PROPERTY_VISIT_ROLE_TO_SYSTEM_ROLE[visitFormData.userRole] ?? '';
  const visitedByRoleId = ((allRolesLookup as any[]) || []).find((r: any) => r.name?.toLowerCase() === systemRoleName.toLowerCase())?.id?.toString() ?? '';

  const { data: allVisitedByOptions } = useFetch({ service: PropertyVisitService, func: 'visitedByLookup', payload: { searchText: '', role: visitFormData.userRole, roleId: visitedByRoleId, cleanerServiceTypeId }, dependency: [visitFormData.userRole, visitedByRoleId, cleanerServiceTypeId] });
  const { data: visitedByOptions } = useFetch({ service: PropertyVisitService, func: 'visitedByLookup', payload: { searchText: dropdownSearchData.visitedBy, role: visitFormData.userRole, roleId: visitedByRoleId, cleanerServiceTypeId }, dependency: [dropdownSearchData.visitedBy, visitFormData.userRole, visitedByRoleId, cleanerServiceTypeId] });

  // For inspector: seed the logged-in user into visitedBy options immediately so the disabled
  // dropdown always shows their name even before the async lookup chain completes.
  const seededAllVisitedByOptions = useMemo(() => {
    const arr = (allVisitedByOptions as any[]) || [];
    if (!isInspector || !loggedInUser?.id) return arr;
    const hasSelf = arr.some((o: any) => o.id?.toString() === loggedInUser.id?.toString());
    if (hasSelf) return arr;
    return [{ id: loggedInUser.id?.toString(), name: (loggedInUser as any).full_name ?? '' }, ...arr];
  }, [allVisitedByOptions, isInspector, loggedInUser]);

  const seededVisitedByOptions = useMemo(() => {
    const arr = (visitedByOptions as any[]) || [];
    if (!isInspector || !loggedInUser?.id) return arr;
    const hasSelf = arr.some((o: any) => o.id?.toString() === loggedInUser.id?.toString());
    if (hasSelf) return arr;
    return [{ id: loggedInUser.id?.toString(), name: (loggedInUser as any).full_name ?? '' }, ...arr];
  }, [visitedByOptions, isInspector, loggedInUser]);
  const { data: allVisitStatuses } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitStatus, searchText: '' }, dependency: [] });

  const { data: allCategoryOptions } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitTaskCategory, searchText: '' }, dependency: [] });
  const { data: categoryOptions } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitTaskCategory, searchText: taskDropdownSearchData.category }, dependency: [taskDropdownSearchData.category] });
  const { data: allPriorityOptions } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitTaskPriority, searchText: '' }, dependency: [] });
  const { data: priorityOptions } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitTaskPriority, searchText: taskDropdownSearchData.priority }, dependency: [taskDropdownSearchData.priority] });
  const { data: allStatusOptions } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitTaskStatus, searchText: '' }, dependency: [] });
  const { data: statusOptions } = useFetch({ service: CommonService, func: 'genericLookup', payload: { key: GENERIC_LOOKUP_KEYS.propertyVisitTaskStatus, searchText: taskDropdownSearchData.status }, dependency: [taskDropdownSearchData.status] });

  // --- Derived ---
  const dateBounds = computeVisitDateBounds();
  const dateMaxDate = formatDate(new Date()); // no future dates for anyone
  const dateMinDate = isAdmin ? undefined : dateBounds.minDate; // non-admins restricted to current month window
  const isQuickVisit = visitFormData.visitType === QUICK_VISIT_TYPE_ID;
  const hasAnyDocument = !!reportUrl.trim() || existingDocs.some((d) => d.document_type !== 'url') || !!pendingPdfFile || pendingImageFiles.length > 0;
  const documentRequired = !isQuickVisit && !hasAnyDocument;

  // --- Visit form handlers ---
  const handleVisitInputChange = useCallback((event: any, name: string) => {
    if (name === VALIDATION_FIELD_MAPPING.userRole && !isInspector) {
      setVisitFormData((prev) => ({ ...prev, [name]: event.target.value, visitedBy: '' }));
    } else {
      if (name === VALIDATION_FIELD_MAPPING.visitType) autoSetVisitTypeRef.current = false;
      setVisitFormData((prev) => ({ ...prev, [name]: event.target.value }));
    }
  }, [isInspector]);

  const handleVisitInputBlur = useCallback((event: any, name: string) => {
    const newValidation = validateVisitForm({ ...visitFormData, [name]: event.target.value }, name);
    setVisitValidation((prev) => {
      const filtered = prev.fields.filter((f) => f.field !== name);
      return { validate: filtered.length === 0 && newValidation.fields.length === 0, fields: [...filtered, ...newValidation.fields] };
    });
  }, [visitFormData]);

  const handleDropdownSearchChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleTaskDropdownSearchChange = useCallback((event: any, name: string) => {
    setTaskDropdownSearchData((prev) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleSaveVisit = useCallback(async () => {
    const newValidation = validateVisitForm(visitFormData);
    setVisitValidation(newValidation);
    if (!newValidation.validate) return;

    setVisitSaveLoading(true);
    try {
      if (isAddMode) {
        const payload = {
          property_id: visitFormData.propertyId,
          visit_date: visitFormData.visitDate,
          visit_type: visitFormData.visitType,
          user_role: visitFormData.userRole,
          visited_by_user_id: visitFormData.visitedBy,
          visit_note: visitFormData.visitNote,
          internal_note: visitFormData.internalNote || undefined,
          status: visitFormData.internalStatus || undefined,
        };
        const response: any = await PropertyVisitService.add(payload);
        if (response?.success !== false) {
          toast.success(<Toast message={APP_SUCCESS_MESSAGES.propertyVisitCreated} />, { icon: false });
          navigate(`${Modules.propertyVisits}${RouteParamsTypes.edit}/${response.id}`);
        }
      } else {
        const payload = {
          property_id: visitFormData.propertyId,
          visit_date: visitFormData.visitDate,
          visit_type: visitFormData.visitType,
          user_role: visitFormData.userRole,
          visited_by_user_id: visitFormData.visitedBy,
          visit_note: visitFormData.visitNote,
          internal_note: visitFormData.internalNote || undefined,
          status: visitFormData.internalStatus || undefined,
        };
        const response: any = await PropertyVisitService.edit(id!, payload);
        if (response?.success !== false) {
          toast.success(<Toast message={APP_SUCCESS_MESSAGES.propertyVisitUpdated} />, { icon: false });
        }
      }
    } finally {
      setVisitSaveLoading(false);
    }
  }, [visitFormData, isAddMode, id, navigate]);

  // --- Documents handlers ---
  const validateReportUrl = (value: string): string => {
    if (!value.trim()) return '';
    try { new URL(value.trim()); return ''; } catch { return APP_ERROR_MESSAGES.propertyVisits.add.invalidReportUrl; }
  };

  const handleReportUrlChange = useCallback((value: string) => {
    setReportUrl(value);
    setReportUrlError(validateReportUrl(value));
  }, []);

  const handleAddPdfFile = useCallback((file: File) => {
    setPendingPdfFile(file);
  }, []);

  const handleRemovePendingPdf = useCallback(() => {
    setPendingPdfFile(null);
  }, []);

  const handleAddImageFiles = useCallback((files: FileList) => {
    Array.from(files).forEach((file) => {
      const localId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      const preview = URL.createObjectURL(file);
      setPendingImageFiles((prev) => [...prev, { localId, file, preview }]);
    });
  }, []);

  const handleRemovePendingImage = useCallback((localId: string) => {
    setPendingImageFiles((prev) => {
      const removed = prev.find((f) => f.localId === localId);
      if (removed) URL.revokeObjectURL(removed.preview);
      return prev.filter((f) => f.localId !== localId);
    });
  }, []);

  const handleRemoveExistingDoc = useCallback(async (docId: string) => {
    await PropertyVisitService.deleteDocument(id!, docId);
    setExistingDocs((prev) => prev.filter((d) => d.id !== docId));
  }, [id]);

  const handleSaveDocuments = useCallback(async () => {
    if (!id) return;
    const urlErr = validateReportUrl(reportUrl);
    if (urlErr) { setReportUrlError(urlErr); return; }
    setDocLoading(true);
    try {
      // URL document
      const existingUrlDoc = existingDocs.find((d) => d.document_type === 'url');
      const newUrl = reportUrl.trim();
      const oldUrl = existingUrlDoc?.path ?? '';
      if (newUrl !== oldUrl) {
        if (existingUrlDoc) {
          await PropertyVisitService.deleteDocument(id, existingUrlDoc.id);
          setExistingDocs((prev) => prev.filter((d) => d.id !== existingUrlDoc.id));
        }
        if (newUrl) {
          const res: any = await PropertyVisitService.addUrlDocument(id, newUrl);
          if (res) {
            const newDoc: VisitDocument = { id: res.id?.toString() ?? '', document_type: 'url', path: res.path ?? newUrl, file_url: res.file_url ?? newUrl };
            setExistingDocs((prev) => [...prev, newDoc]);
          }
        }
      }

      // Pending PDF upload
      if (pendingPdfFile) {
        const doc = await PropertyVisitService.chunkedUploadFile(pendingPdfFile, 'pdf', id);
        if (doc) {
          const newDoc: VisitDocument = { id: (doc as any).id?.toString() ?? '', document_type: 'pdf', path: (doc as any).path ?? '', file_url: (doc as any).file_url ?? '' };
          setExistingDocs((prev) => [...prev, newDoc]);
          setPendingPdfFile(null);
        }
      }

      // Pending image uploads
      if (pendingImageFiles.length > 0) {
        const uploaded: VisitDocument[] = [];
        for (const pending of pendingImageFiles) {
          const doc = await PropertyVisitService.chunkedUploadFile(pending.file, 'image', id);
          if (doc) {
            uploaded.push({ id: (doc as any).id?.toString() ?? '', document_type: 'image', path: (doc as any).path ?? '', file_url: (doc as any).file_url ?? '' });
            URL.revokeObjectURL(pending.preview);
          }
        }
        setExistingDocs((prev) => [...prev, ...uploaded]);
        setPendingImageFiles([]);
      }

      toast.success(<Toast message={APP_SUCCESS_MESSAGES.propertyVisitDocumentsSaved} />, { icon: false });
    } finally {
      setDocLoading(false);
    }
  }, [id, existingDocs, reportUrl, pendingPdfFile, pendingImageFiles]);

  // --- Task handlers ---
  const handleOpenAddTask = useCallback(() => {
    setEditingTask(null);
    setTaskFormData(initialTaskFormData);
    setTaskValidation(initialValidation);
    setTaskModalOpen(true);
  }, []);

  const handleOpenEditTask = useCallback((task: Task) => {
    setEditingTask(task);
    const [m, d, y] = (task.due_date ?? '').split('/');
    const isoDate = m && d && y
      ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
      : task.due_date;
    setTaskFormData({ description: task.description, category: task.category, priority: task.priority, status: task.status, due_date: isoDate });
    setTaskValidation(initialValidation);
    setTaskModalOpen(true);
  }, []);

  const handleCloseTaskModal = useCallback(() => {
    setTaskModalOpen(false);
    setEditingTask(null);
    setTaskDropdownSearchData(initialTaskDropdownSearchData);
  }, []);

  const handleTaskFieldChange = useCallback((event: any, name: string) => {
    setTaskFormData((prev) => ({ ...prev, [name]: event.target.value }));
  }, []);

  const handleTaskFieldBlur = useCallback((event: any, name: string) => {
    const newVal = validateTaskForm({ ...taskFormData, [name]: event.target.value }, name, !!editingTask);
    setTaskValidation((prev) => {
      const filtered = prev.fields.filter((f) => f.field !== name);
      return { validate: filtered.length === 0 && newVal.fields.length === 0, fields: [...filtered, ...newVal.fields] };
    });
  }, [taskFormData, editingTask]);

  const refreshTasks = useCallback(async () => {
    const [refreshed, visitData] = await Promise.all([
      PropertyVisitService.getVisitTasks(id!),
      PropertyVisitService.get(id!),
    ]);
    setTasks(refreshed);
    setPendingTasksCount((visitData as any)?.pending_tasks_count ?? 0);
  }, [id]);

  const handleSaveTask = useCallback(async () => {
    const newValidation = validateTaskForm(taskFormData, undefined, !!editingTask);
    setTaskValidation(newValidation);
    if (!newValidation.validate) return;

    setTaskLoading(true);
    try {
      if (editingTask) {
        await PropertyVisitService.editVisitTask(id!, editingTask.id, taskFormData);
      } else {
        await PropertyVisitService.addVisitTask(id!, taskFormData);
      }
      await refreshTasks();
      toast.success(<Toast message={APP_SUCCESS_MESSAGES.propertyVisitTaskSaved} />, { icon: false });
      setTaskModalOpen(false);
      setEditingTask(null);
    } finally {
      setTaskLoading(false);
    }
  }, [taskFormData, editingTask, id, refreshTasks]);

  const handleOpenDeleteTask = useCallback((taskId: string) => {
    setDeletingTaskId(taskId);
    setTaskDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteTask = useCallback(() => {
    setTaskDeleteModalOpen(false);
    setDeletingTaskId(null);
  }, []);

  const handleConfirmDeleteTask = useCallback(async () => {
    if (!deletingTaskId) return;
    setTaskLoading(true);
    try {
      await PropertyVisitService.deleteVisitTask(id!, deletingTaskId);
      await refreshTasks();
      toast.success(<Toast message={APP_SUCCESS_MESSAGES.propertyVisitTaskDeleted} />, { icon: false });
      setTaskDeleteModalOpen(false);
      setDeletingTaskId(null);
    } finally {
      setTaskLoading(false);
    }
  }, [deletingTaskId, id, refreshTasks]);

  const handleCancel = useCallback(() => {
    navigate(Modules.propertyVisits);
  }, [navigate]);

  return {
    // mode
    isAddMode, isFormEditable, isAdmin, isInspector, tabsDisabled, pendingTasksCount, internalNoteRequired,
    visitLoading,
    // tab
    activeTab, setActiveTab,
    // visit form
    visitFormData, visitValidation, visitSaveLoading,
    dropdownSearchData, activeGuest, dateMinDate, dateMaxDate, isQuickVisit, documentRequired,
    allProperties, properties, allVisitTypes, visitTypes,
    allUserRoles, userRoles, allVisitedByOptions: seededAllVisitedByOptions, visitedByOptions: seededVisitedByOptions, allVisitStatuses,
    allCategoryOptions, categoryOptions, allPriorityOptions, priorityOptions, allStatusOptions, statusOptions,
    handleVisitInputChange, handleVisitInputBlur, handleDropdownSearchChange, handleSaveVisit,
    // documents
    existingDocs, reportUrl, reportUrlError, pendingPdfFile, pendingImageFiles, docLoading,
    handleReportUrlChange, handleAddPdfFile, handleRemovePendingPdf,
    handleAddImageFiles, handleRemovePendingImage, handleRemoveExistingDoc, handleSaveDocuments,
    // tasks
    tasks, taskModalOpen, taskDeleteModalOpen, editingTask,
    taskFormData, taskValidation, taskLoading,
    handleOpenAddTask, handleOpenEditTask, handleCloseTaskModal,
    taskDropdownSearchData, handleTaskDropdownSearchChange,
    handleTaskFieldChange, handleTaskFieldBlur, handleSaveTask,
    handleOpenDeleteTask, handleCloseDeleteTask, handleConfirmDeleteTask,
    // shared
    handleCancel,
  };
};
